// ============================================
// PAGE - Suscripción Premium
// ============================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card } from '@almacenes/ui';
import {
  usePlan,
  useFlowCustomer,
  useSubscriptionDetail,
  useInvoice,
  useCreateSubscription,
  useRegisterCreditCard,
  useSubscriptionStatus,
  useCancelSubscription,
  useBuscarCupon,
  useAuthStore,
  cuponPendienteStorage
} from '@almacenes/core';
import type { FlowCupon, FlowPlan } from '@almacenes/core';
import { useQueryClient } from '@tanstack/react-query';

// Beneficios Premium
const PREMIUM_BENEFITS = [
  { icon: '⚡', title: 'Múltiples formas de pago', description: 'Acepta efectivo, tarjeta y fiado en una sola venta.' },
  { icon: '🧾', title: 'Folios electrónicos automáticos', description: 'Automatiza la carga de folios para tus boletas electrónicas.' },
  { icon: '🗂', title: 'Categorías personalizadas', description: 'Organiza tus productos como quieras según tu negocio.' },
  { icon: '🏪', title: 'Boletas con tu marca', description: 'Agrega el logo de tu almacén en cada boleta emitida.' },
  { icon: '🚀', title: 'Nuevas funciones continuamente', description: 'Accede primero a nuevas herramientas y mejoras premium.' },
];

export const SuscripcionPremiumPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session } = useAuthStore();
  
  // Determinar tipo de usuario
  const isFreemium = session?.hasFreemiumAccess ?? false;
  const hasPremiumAccess = session?.hasPremiumAccess ?? false;
  const rutAlmacen = session?.almacen?.rut ?? '';
  const userName = session?.user?.nombre ?? '';
  const userEmail = session?.user?.email ?? '';

  // Obtener el planId correcto según el tipo de usuario
  const planIdFreemium = import.meta.env.VITE_FLOW_PLAN_ID_VERSION_ALMACEN_PREMIUM;
  const planIdPremiumOnly = import.meta.env.VITE_FLOW_PLAN_ID;
  const planId = isFreemium ? Number(planIdFreemium) : Number(planIdPremiumOnly);

  // Queries
  const { data: plan, isLoading: isLoadingPlan } = usePlan(planId);
  const { 
    data: customer, 
    isLoading: isLoadingCustomer, 
    error: customerError 
  } = useFlowCustomer(rutAlmacen);

  // Estado para el detalle de suscripción
  const hasSubscription = !!(customer?.status === 200 && customer?.subscriptionId);
  const { 
    data: subscriptionDetail, 
    isLoading: isLoadingSubscription 
  } = useSubscriptionDetail(
    customer?.subscriptionId ?? null, 
    hasSubscription
  );

  // Estado para factura impaga
  const [unpaidInvoiceId, setUnpaidInvoiceId] = useState<number | null>(null);
  const { data: invoiceData } = useInvoice(unpaidInvoiceId, !!unpaidInvoiceId);

  // Estado para verificación de pago pendiente
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState<{
    type: 'success' | 'warning' | 'error';
    title: string;
    message: string;
  } | null>(null);

  // Estado para polling de pago
  const [isWaitingForPayment, setIsWaitingForPayment] = useState(false);
  
  // Estado para modal de cancelación
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Estado para cupón de descuento
  const [cuponCodigo, setCuponCodigo] = useState('');
  const [cuponAplicado, setCuponAplicado] = useState<FlowCupon | null>(null);
  const [cuponError, setCuponError] = useState<string | null>(null);

  // Verificar si hay pago en Flow pero no confirmado en backend
  const lastInvoice = subscriptionDetail?.invoices?.[subscriptionDetail.invoices.length - 1];
  const hasPaymentPending = !!(
    hasSubscription && 
    subscriptionDetail &&
    lastInvoice &&
    lastInvoice.status === 1 &&
    !hasPremiumAccess
  );

  // Query para verificar estado (solo cuando hay pago pendiente)
  const { 
    refetch: refetchStatus,
    isLoading: isLoadingStatus
  } = useSubscriptionStatus(rutAlmacen, false);

  // 
  // Mutations
  const createSubscription = useCreateSubscription();
  const registerCreditCard = useRegisterCreditCard();
  const cancelSubscription = useCancelSubscription();
  const buscarCupon = useBuscarCupon();

  // Detectar factura impaga
  useEffect(() => {
    if (subscriptionDetail?.invoices && subscriptionDetail.invoices.length > 0) {
      const lastInvoice = subscriptionDetail.invoices[subscriptionDetail.invoices.length - 1];
      if (lastInvoice.status === 0) {
        setUnpaidInvoiceId(lastInvoice.id);
      }
    }
  }, [subscriptionDetail]);

  // Polling para verificar el pago cuando el usuario está esperando
  useEffect(() => {
    if (!isWaitingForPayment) return;

    const checkPaymentStatus = async () => {
      try {
        const result = await refetchStatus();
        
        if (result.data?.status === 'ACCEPTED') {
          // Pago confirmado - actualizar sesión
          setIsWaitingForPayment(false);
          await queryClient.invalidateQueries({ queryKey: ['session'] });
          await queryClient.refetchQueries({ queryKey: ['session'] });
          
          // Mostrar mensaje de éxito
          setModalConfig({
            type: 'success',
            title: '¡Pago Confirmado! 🎉',
            message: 'Tu pago ha sido procesado exitosamente. La página se actualizará para mostrar la información actualizada.'
          });
          setShowModal(true);
          
          // Recargar la página después de 2 segundos
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
      } catch (error) {
        console.error('Error al verificar estado en polling:', error);
      }
    };

    // Ejecutar cada 5 segundos
    const intervalId = setInterval(checkPaymentStatus, 5000);

    // Limpiar el intervalo al desmontar o cuando se detenga el polling
    return () => clearInterval(intervalId);
  }, [isWaitingForPayment, refetchStatus, queryClient]);

  // Handlers
  const handleRegisterCreditCard = async () => {
    try {
      // Persistir el cupón aplicado para recuperarlo al volver desde Flow
      if (cuponAplicado) {
        cuponPendienteStorage.guardar({ cuponId: cuponAplicado.id, codigo: cuponAplicado.codigo });
      } else {
        cuponPendienteStorage.limpiar();
      }

      const rutSinDv = rutAlmacen.split('-')[0];
      const response = await registerCreditCard.mutateAsync({
        rut: Number(rutSinDv),
        name: userName,
        email: userEmail,
        processPayment: true,
        hasDashboardAccess: true,
      });

      // Redirigir a Flow
      const redirectUrl = `${response.url}?token=${response.token}`;
      window.location.href = redirectUrl;
    } catch (error) {
      console.error('Error al registrar tarjeta:', error);
      alert('Error al procesar el registro de tarjeta. Por favor, intenta nuevamente.');
    }
  };

  const handleCreateManualSubscription = async () => {
    try {
      const rutSinDv = rutAlmacen.split('-')[0];
      const response = await createSubscription.mutateAsync({
        rutAlmacen: Number(rutSinDv),
        planId: planId,
        customerName: userName,
        customerEmail: userEmail,
        ...(cuponAplicado && { cuponId: cuponAplicado.id }),
      });

      if (response.status === 200) {
        navigate('/suscripcion/premium/espera');
      }
    } catch (error) {
      console.error('Error al crear suscripción:', error);
      alert('Error al crear la suscripción. Por favor, intenta nuevamente.');
    }
  };

  const handlePayUnpaidInvoice = () => {
    if (invoiceData?.paymentLink) {
      // Mostrar mensaje informativo primero
      setModalConfig({
        type: 'warning',
        title: 'Proceso de Pago ⏳',
        message: 'El proceso de confirmación puede tomar algunos minutos. Primero Flow confirma tu pago con el banco, y luego nos envía la confirmación a nosotros. Esta página verificará automáticamente el estado cada 5 segundos.'
      });
      setShowModal(true);
      
      // Cerrar el modal automáticamente después de 5 segundos
      setTimeout(() => {
        setShowModal(false);
      }, 5000);
      
      // Abrir el pago en una nueva pestaña
      window.open(invoiceData.paymentLink, '_blank');
      
      // Activar el polling para verificar el pago
      setIsWaitingForPayment(true);
    }
  };

  const handleAddCreditCard = async () => {
    try {
      const rutSinDv = rutAlmacen.split('-')[0];
      const response = await registerCreditCard.mutateAsync({
        rut: Number(rutSinDv),
        name: userName,
        email: userEmail,
        processPayment: false, // Solo registrar tarjeta, no procesar pago
        hasDashboardAccess: hasPremiumAccess,
      });
      
      // Redirigir a Flow para registrar la tarjeta
      const redirectUrl = `${response.url}?token=${response.token}`;
      window.location.href = redirectUrl;
    } catch (error) {
      console.error('Error al registrar tarjeta:', error);
      alert('Error al procesar el registro de tarjeta. Por favor, intenta nuevamente.');
    }
  };

  const handleVerifyPayment = async () => {
    try {
      setIsCheckingStatus(true);
      const result = await refetchStatus();
      
      if (result.data?.status === 'ACCEPTED') {
        // Actualizar la sesión para reflejar el cambio
        await queryClient.invalidateQueries({ queryKey: ['session'] });
        await queryClient.refetchQueries({ queryKey: ['session'] });
        
        // Mostrar mensaje de éxito
        setModalConfig({
          type: 'success',
          title: '¡Pago Confirmado! 🎉',
          message: 'Tu acceso premium ha sido activado exitosamente. La página se actualizará en unos segundos.'
        });
        setShowModal(true);
        
        // Recargar la página después de 2 segundos
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        setModalConfig({
          type: 'warning',
          title: 'Pago en Proceso',
          message: 'El pago aún está siendo procesado por Flow. Por favor, intenta verificar nuevamente en unos minutos.'
        });
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error al verificar estado:', error);
      setModalConfig({
        type: 'error',
        title: 'Error de Verificación',
        message: 'No pudimos verificar el estado del pago en este momento. Por favor, intenta nuevamente o contacta con soporte.'
      });
      setShowModal(true);
    } finally {
      setIsCheckingStatus(false);
    }
  };

  const handleBuscarCupon = async () => {
    const codigo = cuponCodigo.trim().toUpperCase();
    if (!codigo) return;

    setCuponError(null);
    try {
      const result = await buscarCupon.mutateAsync(codigo);

      if (new Date(result.fechaExpiracion) < new Date()) {
        setCuponAplicado(null);
        setCuponError('Este cupón ha expirado.');
        return;
      }

      if (result.redencionesDisponibles <= 0) {
        setCuponAplicado(null);
        setCuponError('Este cupón ya no tiene redenciones disponibles.');
        return;
      }

      setCuponAplicado(result);
    } catch (error) {
      console.error('Error al buscar cupón:', error);
      setCuponAplicado(null);
      setCuponError('No encontramos un cupón válido con ese código.');
    }
  };

  const handleQuitarCupon = () => {
    setCuponAplicado(null);
    setCuponCodigo('');
    setCuponError(null);
  };

  const handleCancelSubscription = async () => {
    if (!subscriptionDetail) return;
    
    try {
      const rutSinDv = Number(rutAlmacen.split('-')[0]);
      await cancelSubscription.mutateAsync({
        rutAlmacen: rutSinDv,
        subscriptionId: subscriptionDetail.subscriptionId,
      });
      
      // Cerrar modal de confirmación
      setShowCancelModal(false);
      
      // Mostrar mensaje informativo
      const endDate = formatDate(subscriptionDetail.period_end);
      setModalConfig({
        type: 'success',
        title: 'Suscripción Cancelada',
        message: `Tu suscripción ha sido cancelada exitosamente. Podrás seguir disfrutando de los beneficios premium hasta el ${endDate}, fecha en que finaliza tu período actual. La página se actualizará en unos segundos.`
      });
      setShowModal(true);

      // Recargar la página después de 3 segundos para mostrar el estado actualizado
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } catch (error) {
      console.error('Error al cancelar suscripción:', error);
      setModalConfig({
        type: 'error',
        title: 'Error al Cancelar',
        message: 'No pudimos cancelar tu suscripción en este momento. Por favor, intenta nuevamente o contacta con soporte.'
      });
      setShowModal(true);
    }
  };

  // Loading state
  if (isLoadingPlan || isLoadingCustomer || (hasSubscription && isLoadingSubscription)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          <p className="text-body-secondary">Cargando información...</p>
        </div>
      </div>
    );
  }

  // Cliente sin suscripción (nuevo usuario o con datos personales sin suscripción)
  const isNewUser = 
    customerError || 
    (customer?.status === 404 && customer?.code === 'LOCAL_RECORD_NOT_FOUND') ||
    (customer?.status === 200 && !customer?.subscriptionId);

  if (isNewUser) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="mx-auto max-w-6xl">
          {/* Header */}
          <div className="mb-12 text-center">
            <div className="mb-4 text-6xl">🚀</div>
            <h1 className="text-title-1 mb-3 text-4xl font-bold">¡Activa tu cuenta!</h1>
            <p className="text-body-secondary text-lg">
              Elige una de las opciones de suscripción para comenzar a disfrutar de todas las funcionalidades de Almacenes Digitales.
            </p>
          </div>

          {/* Cupón de Descuento */}
          <Card className="mb-8 shadow-md">
            <CuponInputSection
              codigo={cuponCodigo}
              onCodigoChange={(value) => {
                setCuponCodigo(value);
                setCuponError(null);
              }}
              onBuscar={handleBuscarCupon}
              onQuitar={handleQuitarCupon}
              cuponAplicado={cuponAplicado}
              error={cuponError}
              isLoading={buscarCupon.isPending}
            />
          </Card>

          {/* Opciones de Suscripción */}
          <div className="grid gap-8 md:grid-cols-2">
            {/* Opción 1: Cargo Automático (Recomendado) */}
            <Card className="relative shadow-lg hover:shadow-xl transition-shadow duration-300 border-2">
              {/* Badge Recomendado */}
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 badge-premium px-6 py-2 rounded-full text-sm font-bold shadow-md">
                RECOMENDADO
              </div>
              
              <div className="pt-6">
                <h3 className="text-center text-gray-600 text-sm font-semibold mb-2 uppercase tracking-wide">
                  PREMIUM MENSUAL + CARGO AUTOMÁTICO
                </h3>
                
                {/* Precio */}
                {plan && <PrecioPlan plan={plan} cupon={cuponAplicado} />}

                <p className="text-body-secondary mb-6 text-sm text-center">
                  Registra tu tarjeta una sola vez y nosotros haremos el cobro automático cada mes. Sin interrupciones ni recordatorios, tu suscripción siempre activa.
                </p>

                {/* Características según tipo de usuario */}
                <div className="mb-8 space-y-3">
                  {isFreemium ? (
                    <>
                      {PREMIUM_BENEFITS.slice(0, 5).map((benefit, index) => (
                        <FeatureItem key={index} text={benefit.title} />
                      ))}
                    </>
                  ) : (
                    <>
                      <FeatureItem text="Inventario" />
                      <FeatureItem text="Venta" />
                      <FeatureItem text="Reporte" />
                      <FeatureItem text="Fiados" />
                      <FeatureItem text="Detalle Boleta" />
                      <FeatureItem text="Ecommerce" />
                      <FeatureItem text="Boleta Electrónica" />
                      <FeatureItem text="Resumen SII" />
                      <FeatureItem text="Cuadratura Caja" />
                      <FeatureItem text="Configuraciones" />
                      <FeatureItem text="...y otras funcionalidades avanzadas" />
                    </>
                  )}
                </div>

                <Button
                  variant="primary"
                  className="w-full text-lg py-4 font-bold btn-premium"
                  onClick={handleRegisterCreditCard}
                  isLoading={registerCreditCard.isPending}
                >
                  💳 REGISTRA TARJETA
                </Button>
              </div>
            </Card>

            {/* Opción 2: Pago Manual */}
            <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300 border-2">
              <div className="pt-4">
                <h3 className="text-center text-gray-600 text-sm font-semibold mb-2 uppercase tracking-wide">
                  PREMIUM MENSUAL
                </h3>
                
                {/* Precio */}
                {plan && <PrecioPlan plan={plan} cupon={cuponAplicado} />}

                <p className="text-body-secondary mb-6 text-sm text-center">
                  Recibirás un correo electrónico con el link de pago cada mes para renovar tu suscripción.
                </p>

                {/* Características según tipo de usuario */}
                <div className="mb-8 space-y-3">
                  {isFreemium ? (
                    <>
                      {PREMIUM_BENEFITS.slice(0, 5).map((benefit, index) => (
                        <FeatureItem key={index} text={benefit.title} />
                      ))}
                    </>
                  ) : (
                    <>
                      <FeatureItem text="Inventario" />
                      <FeatureItem text="Venta" />
                      <FeatureItem text="Reporte" />
                      <FeatureItem text="Fiados" />
                      <FeatureItem text="Detalle Boleta" />
                      <FeatureItem text="Ecommerce" />
                      <FeatureItem text="Boleta Electrónica" />
                      <FeatureItem text="Resumen SII" />
                      <FeatureItem text="Cuadratura Caja" />
                      <FeatureItem text="Configuraciones" />
                      <FeatureItem text="...y otras funcionalidades avanzadas" />
                    </>
                  )}
                </div>

                <Button
                  variant="primary"
                  className="w-full text-lg py-4 font-bold btn-premium"
                  onClick={handleCreateManualSubscription}
                  isLoading={createSubscription.isPending}
                >
                  PAGAR
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Cliente con suscripción activa
  if (hasSubscription && subscriptionDetail) {
    const invoiceToShow = subscriptionDetail.invoices[subscriptionDetail.invoices.length - 1];
    const statusText = subscriptionDetail.status === 1 ? 'Activa' : 
                       subscriptionDetail.status === 0 ? 'Impaga' : 
                       subscriptionDetail.status === 4 ? 'Cancelada' : 'Anulada';
    const statusColor = subscriptionDetail.status === 1 ? 'text-success' : 
                        subscriptionDetail.status === 0 ? 'text-warning' : 'text-error';
    const isCancelled = subscriptionDetail.status === 4;

    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="mx-auto max-w-4xl">
          {/* Header */}
          <div className="mb-8 text-center">
            <h1 className="text-title-1 mb-2">Mi Suscripción</h1>
            <p className="text-body-secondary">
              Detalles de tu suscripción premium
            </p>
          </div>

          {/* Alerta de Pago Pendiente de Confirmación */}
          {hasPaymentPending && (
            <Card className="mb-6 border-warning bg-yellow-50">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-title-3 text-warning mb-2">
                    Pago Pendiente de Confirmación
                  </h3>
                  <p className="text-body-secondary text-sm mb-4">
                    Flow ha recibido tu pago exitosamente, pero aún estamos esperando la confirmación 
                    para activar tu acceso premium. Este proceso puede tomar algunos minutos.
                  </p>
                  <Button
                    variant="warning"
                    size="sm"
                    onClick={handleVerifyPayment}
                    isLoading={isCheckingStatus || isLoadingStatus}
                  >
                    Verificar Estado del Pago
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Banner: Esperando confirmación de pago */}
          {isWaitingForPayment && (
            <Card className="mb-6 border-primary bg-blue-50">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
                </div>
                <div className="flex-1">
                  <h3 className="text-title-3 text-primary mb-2">
                    Esperando Confirmación de Pago
                  </h3>
                  <p className="text-body-secondary text-sm mb-2">
                    Hemos abierto la ventana de pago en una nueva pestaña. Esta página se actualizará automáticamente 
                    cuando detectemos que el pago ha sido procesado.
                  </p>
                  <p className="text-body-secondary text-xs">
                    Verificando estado cada 5 segundos...
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setIsWaitingForPayment(false)}
                    className="mt-3"
                  >
                    Cancelar Espera
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Información de Suscripción */}
          <Card className="mb-6">
            <div className="mb-6 border-b border-gray-200 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-title-2">{subscriptionDetail.plan_name}</h2>
                  <p className="text-body-secondary text-sm">
                    Suscripción creada el {formatDate(subscriptionDetail.created)}
                  </p>
                </div>
                {subscriptionDetail.status === 1 ? (
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-600 text-white text-base font-bold shadow-md">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    ACTIVA
                  </span>
                ) : isCancelled ? (
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-600 text-white text-base font-bold shadow-md">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    CANCELADA
                  </span>
                ) : (
                  <span className={`text-title-3 ${statusColor}`}>
                    {statusText}
                  </span>
                )}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <InfoRow 
                label="Período actual" 
                value={`${formatDate(subscriptionDetail.period_start)} - ${formatDate(subscriptionDetail.period_end)}`} 
              />
              {!isCancelled && (
                <InfoRow 
                  label="Próxima factura" 
                  value={formatDate(subscriptionDetail.next_invoice_date)} 
                />
              )}
              {isCancelled && subscriptionDetail.cancel_at && (
                <InfoRow 
                  label="Fecha de cancelación" 
                  value={formatDate(subscriptionDetail.cancel_at)}
                  valueClassName="text-error"
                />
              )}
            </div>

            {/* Opción de cancelación (solo cuando está activa) */}
            {subscriptionDetail.status === 1 && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <p className="text-gray-500 text-xs mb-2">
                  Si quieres cancelar la suscripción{' '}
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="text-gray-600 underline hover:text-gray-800 transition-colors"
                    disabled={cancelSubscription.isPending}
                  >
                    presiona aquí para cancelar
                  </button>
                </p>
              </div>
            )}
          </Card>

          {/* Opciones de Reactivación (solo cuando está cancelada) */}
          {isCancelled && (
            <Card className="mb-6 bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200">
              <div className="mb-8 text-center">
                <h3 className="text-title-1 mb-2 text-3xl font-bold" style={{ color: '#11194F' }}>
                  ¿Cómo quieres reactivar tu suscripción?
                </h3>
                <p className="text-body-secondary text-base">
                  Elige la opción que mejor se adapte a ti
                </p>
              </div>

              {/* Cupón de Descuento */}
              <div className="mb-8 rounded-2xl bg-white border-2 border-purple-200 p-6 shadow-md">
                <CuponInputSection
                  codigo={cuponCodigo}
                  onCodigoChange={(value) => {
                    setCuponCodigo(value);
                    setCuponError(null);
                  }}
                  onBuscar={handleBuscarCupon}
                  onQuitar={handleQuitarCupon}
                  cuponAplicado={cuponAplicado}
                  error={cuponError}
                  isLoading={buscarCupon.isPending}
                />
              </div>

              <div className="grid gap-6 lg:grid-cols-2">
                {/* Opción 1: Cargo Automático (Recomendado) */}
                <div className="relative rounded-2xl bg-white border-2 border-purple-300 p-6 shadow-lg hover:shadow-xl transition-all hover:scale-105">
                  {/* Badge Recomendado */}
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 badge-premium px-4 py-1 rounded-full text-xs font-bold shadow-md">
                    RECOMENDADO
                  </div>
                  
                  <div className="text-center mb-4 mt-2">
                    <div className="text-5xl mb-3">💳</div>
                    <h4 className="text-title-2 text-2xl font-bold mb-2" style={{ color: '#11194F' }}>
                      Cargo Automático
                    </h4>
                    
                    {/* Precio */}
                    {plan && <PrecioPlan plan={plan} cupon={cuponAplicado} size="sm" />}

                    <p className="text-body-secondary text-sm">
                      Registra tu tarjeta y olvídate de pagos manuales
                    </p>
                  </div>

                  {/* Beneficios con checkmarks */}
                  <div className="mb-6 space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="text-purple-600 text-xl flex-shrink-0">✓</span>
                      <span className="text-body text-sm font-medium">No olvidar pagos</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-purple-600 text-xl flex-shrink-0">✓</span>
                      <span className="text-body text-sm font-medium">Renovación automática</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-purple-600 text-xl flex-shrink-0">✓</span>
                      <span className="text-body text-sm font-medium">Mantienes beneficios activos</span>
                    </div>
                  </div>

                  <Button
                    variant="primary"
                    className="w-full btn-premium text-base py-3 font-bold"
                    onClick={handleRegisterCreditCard}
                    isLoading={registerCreditCard.isPending}
                  >
                    Registrar Tarjeta
                  </Button>
                </div>

                {/* Opción 2: Pago Manual */}
                <div className="rounded-2xl bg-white border-2 border-gray-300 p-6 shadow-lg hover:shadow-xl transition-all hover:scale-105">
                  <div className="text-center mb-4">
                    <div className="text-5xl mb-3">📧</div>
                    <h4 className="text-title-2 text-2xl font-bold mb-2" style={{ color: '#11194F' }}>
                      Pago Manual
                    </h4>
                    
                    {/* Precio */}
                    {plan && <PrecioPlan plan={plan} cupon={cuponAplicado} size="sm" />}

                    <p className="text-body-secondary text-sm">
                      Recibe un correo con el link de pago cada mes
                    </p>
                  </div>

                  {/* Beneficios con checkmarks */}
                  <div className="mb-6 space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="text-purple-600 text-xl flex-shrink-0">✓</span>
                      <span className="text-body text-sm font-medium">Pago mensual manual</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-purple-600 text-xl flex-shrink-0">✓</span>
                      <span className="text-body text-sm font-medium">Recibes link por correo</span>
                    </div>
                  </div>

                  <Button
                    variant="primary"
                    className="w-full btn-premium text-base py-3 font-bold"
                    onClick={handleCreateManualSubscription}
                    isLoading={createSubscription.isPending}
                  >
                    Reactivar con Pago Manual
                  </Button>
                </div>
              </div>

              {/* Lista de Beneficios Premium */}
              <div className="mt-8 pt-8 border-t-2 border-purple-200">
                <div className="flex items-center justify-center gap-2 mb-6">
                  <span className="text-3xl">👑</span>
                  <h4 className="text-title-2 text-2xl font-bold" style={{ color: '#11194F' }}>
                    Beneficios Premium
                  </h4>
                </div>
                
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {PREMIUM_BENEFITS.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-white/50">
                      <span className="text-2xl flex-shrink-0">{benefit.icon}</span>
                      <div>
                        <h5 className="text-body font-bold text-sm mb-1">{benefit.title}</h5>
                        <p className="text-body-secondary text-xs">{benefit.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}

          {/* Información del Cliente */}
          <Card className="mb-6">
            <h3 className="text-title-3 mb-4">Información del Cliente</h3>
            
            <div className="space-y-3">
              <InfoRow 
                label="Nombre" 
                value={customer?.name || 'No disponible'} 
              />
              
              {customer?.creditCardType && customer?.last4CardDigits && (
                <>
                  <InfoRow 
                    label="Tipo de Tarjeta" 
                    value={customer.creditCardType} 
                  />
                  
                  {/* Últimos 4 dígitos con botón cambiar tarjeta */}
                  <div>
                    <p className="text-body-secondary text-sm">Últimos 4 dígitos</p>
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-body font-medium text-text-primary">
                        **** **** **** {customer.last4CardDigits}
                      </p>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleAddCreditCard}
                        isLoading={registerCreditCard.isPending}
                      >
                        Cambiar Tarjeta
                      </Button>
                    </div>
                  </div>
                  
                  <InfoRow 
                    label="Método de Pago" 
                    value={customer.payMode === 'auto' ? 'Cargo Automático' : 'Manual'} 
                  />
                </>
              )}
              
              {(!customer?.creditCardType || !customer?.last4CardDigits) && (
                <div className="rounded-lg bg-blue-50 border border-primary p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <svg className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="flex-1">
                      <p className="text-body-secondary text-sm mb-1">
                        {customer?.payMode === 'manual' 
                          ? 'Suscripción de pago manual - Recibirás un correo con el link de pago cada mes.'
                          : 'No hay información de tarjeta registrada.'
                        }
                      </p>
                      <p className="text-body text-sm font-medium">
                        ¿Deseas activar el cargo automático? Registra tu tarjeta y olvídate de pagos manuales.
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleAddCreditCard}
                    isLoading={registerCreditCard.isPending}
                    className="btn-premium"
                  >
                    💳 Registrar Tarjeta para Cargo Automático
                  </Button>
                </div>
              )}
            </div>
          </Card>

          {/* Última Factura */}
          {invoiceToShow && (
            <Card className={`${invoiceToShow.status === 0 ? 'border-2 border-warning bg-yellow-50' : ''}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-title-3">Última Factura</h3>
                {invoiceToShow.status === 0 && (
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-warning text-white text-sm font-semibold">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    PAGO PENDIENTE
                  </span>
                )}
              </div>
              
              <div className="space-y-3">
                <InfoRow 
                  label="Creada" 
                  value={formatDate(invoiceToShow.created)} 
                />
                <InfoRow 
                  label="Monto" 
                  value={`${invoiceToShow.currency === 'CLP' ? '$' : invoiceToShow.currency} ${new Intl.NumberFormat('es-CL').format(invoiceToShow.amount)}`} 
                />
                <InfoRow 
                  label="Período" 
                  value={`${formatDate(invoiceToShow.period_start)} - ${formatDate(invoiceToShow.period_end)}`} 
                />
                <InfoRow 
                  label="Estado" 
                  value={
                    invoiceToShow.status === 1 ? 'Pagada' : 
                    invoiceToShow.status === 0 ? 'Impaga' : 'Anulada'
                  }
                  valueClassName={
                    invoiceToShow.status === 1 ? 'text-success' : 
                    invoiceToShow.status === 0 ? 'text-warning' : 'text-error'
                  }
                />

                {/* Botón para pagar factura impaga */}
                {invoiceToShow.status === 0 && invoiceData?.paymentLink && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <Button
                      variant="primary"
                      className="w-full btn-premium"
                      onClick={handlePayUnpaidInvoice}
                      disabled={isWaitingForPayment}
                    >
                      {isWaitingForPayment ? (
                        <>
                          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2"></span>
                          Esperando Confirmación...
                        </>
                      ) : (
                        <>
                          💳 Pagar Factura (Abre en Nueva Pestaña)
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>

        {/* Modal de verificación */}
        {showModal && modalConfig && (
          <VerificationModal
            type={modalConfig.type}
            title={modalConfig.title}
            message={modalConfig.message}
            onClose={() => setShowModal(false)}
          />
        )}

        {/* Modal de confirmación de cancelación */}
        {showCancelModal && subscriptionDetail && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    ¿Estás seguro?
                  </h3>
                  <p className="text-gray-600">
                    Si cancelas tu suscripción, seguirás teniendo acceso a todos los beneficios premium hasta el final de tu período actual ({formatDate(subscriptionDetail.period_end)}). Después de esa fecha, tu cuenta volverá al plan gratuito.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button
                  variant="neutral"
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1"
                  disabled={cancelSubscription.isPending}
                >
                  No, mantener
                </Button>
                <Button
                  variant="danger"
                  onClick={handleCancelSubscription}
                  className="flex-1"
                  disabled={cancelSubscription.isPending}
                >
                  {cancelSubscription.isPending ? 'Cancelando...' : 'Sí, cancelar'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

// Componentes auxiliares
const VerificationModal = ({
  type,
  title,
  message,
  onClose
}: {
  type: 'success' | 'warning' | 'error';
  title: string;
  message: string;
  onClose: () => void;
}) => {
  const icons = {
    success: (
      <svg className="h-16 w-16 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    warning: (
      <svg className="h-16 w-16 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    error: (
      <svg className="h-16 w-16 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  };

  const bgColors = {
    success: 'bg-green-50',
    warning: 'bg-yellow-50',
    error: 'bg-red-50'
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all animate-in fade-in zoom-in duration-200">
        <div className={`px-6 pt-8 pb-6 ${bgColors[type]}`}>
          <div className="flex flex-col items-center text-center">
            <div className="mb-4">
              {icons[type]}
            </div>
            <h3 className="text-title-2 mb-2">{title}</h3>
            <p className="text-body-secondary text-sm">{message}</p>
          </div>
        </div>
        <div className="px-6 py-4 bg-gray-50 flex justify-center">
          <Button
            variant="primary"
            onClick={onClose}
            className="min-w-32"
          >
            Entendido
          </Button>
        </div>
      </div>
    </div>
  );
};

const PRECIO_PLAN_SIZES = {
  lg: {
    container: 'mb-6',
    simbolo: 'text-5xl font-bold',
    monto: 'text-6xl font-bold',
    periodo: 'text-gray-600 text-lg',
    tachado: 'text-gray-400 text-lg line-through',
  },
  sm: {
    container: 'mb-3',
    simbolo: 'text-3xl font-bold',
    monto: 'text-4xl font-bold',
    periodo: 'text-gray-600 text-sm',
    tachado: 'text-gray-400 text-sm line-through',
  },
};

// Flow cobra el monto CLP con IVA incluido. Para que el precio se vea más
// atractivo, en la página se muestra el monto neto (+ IVA) en vez del total.
// El descuento del cupón sigue calculándose sobre el monto total (con IVA),
// que es lo que Flow efectivamente cobra — solo cambia cómo se presenta.
const IVA_RATE = 0.19;

const PrecioPlan = ({
  plan,
  cupon,
  size = 'lg',
}: {
  plan: FlowPlan;
  cupon: FlowCupon | null;
  size?: 'lg' | 'sm';
}) => {
  const montoOriginal = Number(plan.amount);
  const isUF = plan.currency === 'UF';
  const isCLP = !isUF;
  const simbolo = isUF ? 'UF' : '$';
  const formatear = (valor: number) =>
    isUF ? valor.toFixed(3) : new Intl.NumberFormat('es-CL').format(valor);
  const aMontoNeto = (valor: number) => Math.round(valor / (1 + IVA_RATE));
  const s = PRECIO_PLAN_SIZES[size];

  const montoOriginalMostrado = isCLP ? aMontoNeto(montoOriginal) : montoOriginal;
  const sufijoPeriodo = isCLP ? '+ IVA /Mes' : '/Mes';

  if (!cupon) {
    return (
      <div className={`text-center ${s.container}`}>
        <div className="flex items-baseline justify-center gap-1">
          <span className={s.simbolo} style={{ color: '#11194F' }}>{simbolo}</span>
          <span className={s.monto} style={{ color: '#11194F' }}>{formatear(montoOriginalMostrado)}</span>
          <span className={s.periodo}>{sufijoPeriodo}</span>
        </div>
      </div>
    );
  }

  const descuento = cupon.tipoDescuento === 'PORCENTAJE'
    ? montoOriginal * ((cupon.porcentajeDescuento ?? 0) / 100)
    : (cupon.montoDescuento ?? 0);
  const montoConDescuento = Math.max(0, montoOriginal - descuento);
  const montoConDescuentoMostrado = isCLP ? aMontoNeto(montoConDescuento) : montoConDescuento;

  return (
    <div className={`text-center ${s.container}`}>
      <p className={s.tachado}>
        {simbolo}{formatear(montoOriginalMostrado)}{isCLP ? ' + IVA' : ''}
      </p>
      <div className="flex items-baseline justify-center gap-1">
        <span className={`${s.simbolo} text-success`}>{simbolo}</span>
        <span className={`${s.monto} text-success`}>{formatear(montoConDescuentoMostrado)}</span>
        <span className={s.periodo}>{sufijoPeriodo}</span>
      </div>
      <span className="inline-block mt-1 px-3 py-1 rounded-full bg-green-100 text-success text-xs font-bold">
        Cupón {cupon.codigo} aplicado
      </span>
    </div>
  );
};

const CuponIcon = () => (
  <div
    className="h-11 w-11 rounded-full flex items-center justify-center flex-shrink-0"
    style={{ backgroundColor: 'var(--color-success)' }}
  >
    <svg
      className="h-6 w-6 text-white"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4.5 5.5A2 2 0 002.5 7.5v1.7a1.4 1.4 0 010 2.6v1.7a2 2 0 002 2h15a2 2 0 002-2v-1.7a1.4 1.4 0 010-2.6V7.5a2 2 0 00-2-2h-15z" />
      <line x1="9" y1="5.5" x2="9" y2="15.5" strokeDasharray="2 2" />
      <circle cx="13.7" cy="8.7" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="17.3" cy="12.3" r="1.1" fill="currentColor" stroke="none" />
      <line x1="17.8" y1="8.2" x2="13.2" y2="12.8" />
    </svg>
  </div>
);

const CuponInputSection = ({
  codigo,
  onCodigoChange,
  onBuscar,
  onQuitar,
  cuponAplicado,
  error,
  isLoading,
}: {
  codigo: string;
  onCodigoChange: (value: string) => void;
  onBuscar: () => void;
  onQuitar: () => void;
  cuponAplicado: FlowCupon | null;
  error: string | null;
  isLoading: boolean;
}) => (
  <>
    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
      <div className="lg:flex-1 flex items-start gap-3">
        <CuponIcon />
        <div>
          <h3 className="text-title-3 mb-1">¿Tienes un cupón de descuento?</h3>
          <p className="text-body-secondary text-sm">
            Ingresa tu código y aplícalo para ver el precio con descuento.
          </p>
        </div>
      </div>

      <div className="lg:flex-1">
        {!cuponAplicado ? (
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={codigo}
              onChange={(e) => onCodigoChange(e.target.value.toUpperCase())}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onBuscar();
              }}
              placeholder="Ej: NAVIDAD2026"
              className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-base focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={isLoading}
            />
            <Button
              variant="secondary"
              onClick={onBuscar}
              isLoading={isLoading}
              disabled={!codigo.trim()}
            >
              Aplicar Cupón
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 rounded-lg bg-green-50 border border-success px-4 py-3">
            <div>
              <p className="text-body font-bold text-success">
                ✓ {cuponAplicado.nombre} ({cuponAplicado.codigo})
              </p>
              <p className="text-body-secondary text-sm">
                {cuponAplicado.tipoDescuento === 'PORCENTAJE'
                  ? `${cuponAplicado.porcentajeDescuento}% de descuento`
                  : `$${new Intl.NumberFormat('es-CL').format(cuponAplicado.montoDescuento ?? 0)} de descuento`}
              </p>
            </div>
            <Button variant="neutral" size="sm" onClick={onQuitar}>
              Quitar
            </Button>
          </div>
        )}
      </div>
    </div>

    {error && (
      <p className="text-error text-sm mt-3">{error}</p>
    )}
  </>
);

const FeatureItem = ({ text }: { text: string }) => (
  <div className="flex items-start gap-3">
    <span className="text-success text-xl mt-0.5 flex-shrink-0">✓</span>
    <span className="text-body text-base">{text}</span>
  </div>
);

const InfoRow = ({ 
  label, 
  value, 
  valueClassName = 'text-text-primary' 
}: { 
  label: string; 
  value: string; 
  valueClassName?: string;
}) => (
  <div>
    <p className="text-body-secondary text-sm">{label}</p>
    <p className={`text-body font-medium ${valueClassName}`}>{value}</p>
  </div>
);

// Utilidades
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('es-CL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
};
